import {Box, Button, styled} from "@mui/material";
import Base from "../../../components/base";
import {buildPath, formatDateInfo, scale} from "../../../utils";
import BaseHeader from "../../../components/base/header";
import BaseTitle from "../../../components/base/title";
import LABEL from "../../../resources/label";
import theme, {FONTS} from "../../../constants/theme";
import Text from "../../../components/text";
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import CustomSvg from "../../../components/custom-svg";
import {GOCCIA_RIFLESSO} from "../../../constants/svgs";
import ThemedText from "../../../components/text";
import DateCard from "../../../components/date-card";
import DateRowInfo from "../../../components/date-row-info";
import React, {useEffect, useState} from "react";
import {NewDonationRequest} from "../../../types";
import {useNavigate} from "react-router-dom";
import FullScreenDialog from "../../../components/full-screen-dialog";
import {createNewRaccolta} from "../../../features/operator";

const Wrapper = styled(Box)({

});

const DonationInfo = styled(Box)({
  marginTop: scale(30),
  display: 'grid',
  gridTemplateRows: 'auto auto',
  justifyItems: 'center',
  rowGap: scale(29.96),
});

const DetailGroup = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'auto auto',
  columnGap: scale(40),
  justifyContent: 'center',
  justifyItems: 'center',
})

const InfoGroup = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: scale(12),
  flexGrow: 1,
  height: scale(240),
  width: scale(250),
})

const ButtonGroup = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  columnGap: scale(11),
  marginTop: 'auto',
  marginBottom: scale(49)

});

const ThemedButton = styled(Button)({
  borderRadius: scale(30),
  width: scale(160),
  height: scale(52),
  marginTop: scale(13),
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.lightGray.main,
    cursor: 'not-allowed',
  },
});

const ConfermaCreaRaccolta = () => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newDonation, setNewDonation] = useState<NewDonationRequest | null>(null)

  useEffect(() => {
    const storedData = sessionStorage.getItem('createDonation');
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        const donation: NewDonationRequest = {
          isTemplate: parsed.isTemplateSelected,
          title: parsed.eventName,
          date: new Date(parsed.date),
          address: parsed.address,
          seats: parsed.postazioni,
          am_start: parsed.amTurn && parsed.amTurn.length === 2 ? parsed.amTurn[0] : null,
          am_end: parsed.amTurn && parsed.amTurn.length === 2 ? parsed.amTurn[1] : null,
          pm_start: parsed.pmTurn && parsed.pmTurn.length === 2 ? parsed.pmTurn[0] : null,
          pm_end: parsed.pmTurn && parsed.pmTurn.length === 2 ? parsed.pmTurn[1] : null,
          type: parsed.eventType
        };
        setNewDonation(donation);
      } catch (error) {
        console.error('Errore nel parsing dei dati salvati:', error);
        navigate(buildPath('/operatore/crea-raccolta'))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!newDonation) {
    return null
  }
  const dateInfo = formatDateInfo(newDonation.date)

  return (
    <>
      <FullScreenDialog
        title={'VUOI ABBANDONARE LA CREAZIONE DI UNA NUOVA RACCOLTA?'}
        content={'Se prosegui perderai tutti i dati inseriti fino a questo punto'}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={() => {
          sessionStorage.removeItem('createDonation');
          navigate(buildPath('/operatore/calendario'))
        }}
        colorStyle={'blue'}
        cancelText={'ANNULLA'}
        confirmText={'ABBANDONA'}
      />
      <Wrapper>
      <Base style={{ width: scale(900), height: scale(750) }}>
        <BaseHeader/>
        <BaseTitle title={LABEL.NEW_DONATION}/>
        <DonationInfo>
          <Text
            text={newDonation.title}
            fontFamily={FONTS.MONTSERRAT}
            color={theme.palette.text.secondary}
            fontSize={scale(24)}
            fontWeight={'bold'}
            sx={{ textTransform: 'uppercase' }}
          />
          <DetailGroup>
            <DateCard
              dayName={dateInfo.weekday}
              dayNumber={dateInfo.day.toString()}
              month={dateInfo.month.concat('\n')}
              year={dateInfo.year.toString()}
              colorStyle={'primary'}
            />
            <InfoGroup>
              <DateRowInfo
                IconComponent={WatchLaterIcon}
                firstRowText={newDonation.am_start ? newDonation.am_start.concat(' - ').concat(newDonation.am_end) : ''}
                secondRowText={newDonation.pm_start ? newDonation.pm_start.concat(' - ').concat(newDonation.pm_end) : ''}
                colorStyle={'primary'}
              />
              <DateRowInfo
                IconComponent={FmdGoodIcon}
                firstRowText={newDonation.address}
                colorStyle={'primary'}
              />
              {(newDonation.type === 'sangue' || newDonation.type === 'plasma & sangue') && (<DateRowInfo
                IconComponent={
                  <CustomSvg
                    svg={GOCCIA_RIFLESSO}
                    sx={{
                      zIndex: 2,
                      fontSize: scale(36),
                    }}
                    color={'white'}
                  />
                }
                bgColor={theme.palette.blood.main}
                colorStyle={'primary'}
                firstRowText={(!newDonation.isTemplate ? newDonation.seats.toString().concat(' ') : '') + 'Postazioni'}
                secondRowText={'Sangue'}
              />)}
              {(newDonation.type === 'plasma' || newDonation.type === 'plasma & sangue') && (<DateRowInfo
                IconComponent={
                  <CustomSvg
                    svg={GOCCIA_RIFLESSO}
                    sx={{
                      zIndex: 2,
                      fontSize: scale(36),
                    }}
                    color={'white'}
                  />
                }
                bgColor={theme.palette.plasma.main}
                colorStyle={'primary'}
                firstRowText={(!newDonation.isTemplate ? newDonation.seats.toString().concat(' ') : '') + 'Postazioni'}
                secondRowText={'Plasma'}
              />)}
            </InfoGroup>
          </DetailGroup>
        </DonationInfo>
        <ButtonGroup>
          <ThemedButton variant="contained" onClick={() => setDialogOpen(true)}>
            <ThemedText text={'annulla'} fontWeight={600} fontSize={scale(18)} fontFamily={FONTS.MONTSERRAT}/>
          </ThemedButton>
          <ThemedButton
            variant="contained"
            onClick={() => {
              createNewRaccolta(newDonation)
                .then(() => {
                  sessionStorage.removeItem('createDonation');
                  sessionStorage.setItem('successCreation', 'true')
                  navigate(buildPath('/operatore/calendario'))
                })
                .catch((error) => {
                  console.error(error);
                });
            }}
          >
            <ThemedText text={'conferma'} fontWeight={600} fontSize={scale(18)} fontFamily={FONTS.MONTSERRAT}/>
          </ThemedButton>
        </ButtonGroup>
      </Base>
    </Wrapper>
    </>
  );
}

export default ConfermaCreaRaccolta;
